import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useDeployAuth } from "../../../context/DeployAuthContext";
import LimeLogo from "../../../assets/lime-logo-no-bg.png";

const api = axios.create({ baseURL: "", withCredentials: true });

// Matches the poll cadence the backend rate limiter is tuned for (see
// backend/creator/routes/creatorBillingRoutes.js pollLimiter comment — "polled every 15s
// during provisioning (~15 min)").
const POLL_INTERVAL_MS = 15000;

const STEP_LABELS = ["Payment confirmed", "Setting up your database", "Launching your platform", "Going live"];

function Step({ label, state }) {
  // state: "done" | "active" | "waiting"
  return (
    <div className="flex items-center gap-4">
      <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
        {state === "done" && (
          <div className="w-8 h-8 rounded-full bg-lime-600/20 border border-lime-600 flex items-center justify-center">
            <svg className="w-4 h-4 text-lime-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
        {state === "active" && (
          <div className="w-8 h-8 rounded-full bg-lime-900/20 border border-lime-800/50 flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {state === "waiting" && (
          <div className="w-8 h-8 rounded-full border border-gray-800 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-gray-700" />
          </div>
        )}
      </div>
      <span className={`text-sm font-medium transition-colors ${
        state === "done" ? "text-lime-300" :
        state === "active" ? "text-silver-light" :
        "text-silver-dark"
      }`}>
        {label}
      </span>
    </div>
  );
}

export default function CreatorProvisioningPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useDeployAuth();

  const [status, setStatus] = useState(null); // { label, step, steps, estimatedMinutesRemaining, done, storeUrl, adminUrl }
  const [loadError, setLoadError] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);

  const pollRef = useRef(null);

  const poll = async () => {
    try {
      const { data } = await api.get("/api/creator/billing/provision-status");
      setStatus(data);
      if (data.done || data.step === null) {
        // Terminal-ish states: active (done) or degraded/suspended/failed (step === null) —
        // stop polling either way. A degraded/suspended box doesn't self-resolve by polling
        // faster; the fleet's own health FSM handles that.
        clearInterval(pollRef.current);
      }
    } catch {
      setLoadError("Connection lost — retrying…");
      setTimeout(() => setLoadError(""), 4000);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/limelite/creator/start", { replace: true });
      return;
    }

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => clearInterval(pollRef.current);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/api/creator/billing/create-portal-session");
      if (data.url) window.location.href = data.url;
    } catch {
      setPortalLoading(false);
    }
  };

  if (!status) {
    return (
      <div className="bg-black text-silver-light min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
        <div className="w-5 h-5 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isActive = Boolean(status.done);
  // provisionStatusForCustomer() (backend) returns step: null for degraded / suspended / failed
  // — the frontend doesn't get (or need) the raw internal status name, just the friendly label
  // and the fact that it's not a normal progress state.
  const isIssue = !isActive && status.step === null;

  return (
    <div className="bg-black text-silver-light min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-12">
          <img src={LimeLogo} alt="Limelite" className="w-7 h-7" />
          <span className="text-xs uppercase tracking-widest text-lime-500">Limelite Creator</span>
        </div>

        {isIssue ? (
          /* ── Issue state (degraded / suspended / failed) ── */
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-amber-900/20 border border-amber-800 flex items-center justify-center mx-auto mb-6">
              <svg className="w-7 h-7 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <h1 className="text-2xl font-serif font-medium mb-3">Needs attention</h1>
            <p className="text-silver-dark text-sm mb-8 leading-relaxed">{status.label}</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={openBillingPortal}
                disabled={portalLoading}
                className="inline-flex items-center justify-center border border-gray-700 hover:border-gray-500 text-silver-light text-sm font-medium py-2.5 px-6 rounded-xl transition-colors disabled:opacity-40"
              >
                {portalLoading ? "Opening…" : "Manage billing →"}
              </button>
              <Link
                to="/limelite/deploy/abuse-report"
                className="text-sm text-silver-dark hover:text-silver-light transition-colors text-center"
              >
                Contact support →
              </Link>
            </div>
          </div>
        ) : isActive ? (
          /* ── Success state ── */
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-lime-600/20 border border-lime-600 flex items-center justify-center mx-auto mb-6">
              <svg className="w-7 h-7 text-lime-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-serif font-medium mb-3">Your platform is live.</h1>
            <p className="text-silver-dark text-sm mb-8 leading-relaxed">{status.label}</p>
            <div className="flex flex-col gap-3">
              {status.storeUrl && (
                <a
                  href={status.storeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-lime-600 hover:bg-lime-500 text-black font-bold py-3 px-8 rounded-xl transition-colors"
                >
                  Open your platform →
                </a>
              )}
              {status.adminUrl && (
                <a
                  href={status.adminUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center border border-gray-700 hover:border-gray-500 text-silver-light text-sm font-medium py-2.5 px-6 rounded-xl transition-colors"
                >
                  Open your admin →
                </a>
              )}
              <Link
                to="/limelite/creator"
                className="text-sm text-silver-dark hover:text-silver-light transition-colors text-center"
              >
                Back to Limelite Creator →
              </Link>
            </div>
          </div>
        ) : (
          /* ── Progress state ── */
          <>
            <div className="text-center mb-10">
              <h1 className="text-2xl font-serif font-medium mb-2">Setting up your platform</h1>
              <p className="text-silver-dark text-sm">{status.label}</p>
              {typeof status.estimatedMinutesRemaining === "number" && status.estimatedMinutesRemaining > 0 && (
                <p className="text-silver-dark text-xs mt-1">
                  ~{status.estimatedMinutesRemaining} minute{status.estimatedMinutesRemaining === 1 ? "" : "s"} remaining
                </p>
              )}
            </div>

            {/* Steps — backend step: 0=reserved(payment not yet confirmed) 1=pending 2=provisioning_db
                3=provisioning_infra 4=active(handled in the isActive branch above, not here).
                Step 1 "Payment confirmed" always renders done on this page (same rationale as
                StorefrontProvisioningPage.js: you only land here via the Stripe success_url
                redirect, so payment is done from the customer's chair even if the backend
                record briefly still reads "reserved" before the webhook lands). */}
            <div className="space-y-5 mb-10">
              {STEP_LABELS.map((label, i) => {
                const stepIndex = i + 1;
                let state;
                if (stepIndex === 1) state = "done";
                else if (stepIndex === 2) state = status.step >= 3 ? "done" : "active";
                else if (stepIndex === 3) state = status.step >= 3 ? "active" : "waiting";
                else state = "waiting";
                return <Step key={label} label={label} state={state} />;
              })}
            </div>

            {loadError && (
              <p className="text-center text-xs text-amber-600 mt-3">{loadError}</p>
            )}
          </>
        )}

        {/* Footer link */}
        {!isActive && !isIssue && (
          <p className="text-center text-xs text-gray-700 mt-12">
            <Link to="/limelite/creator" className="hover:text-silver-dark transition-colors">
              Back to Limelite Creator
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
